/**
 * Shared tournament presentation helpers.
 *
 * Tournament documents pre-date the richer settings UI, so public and admin
 * code should read through these normalizers instead of branching on every
 * legacy shape. The normalizers are intentionally additive: they never write
 * to Firestore and they retain useful values from older documents.
 */

export const toDate = (value) => {
  if (!value) return null;
  const date =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (!date || typeof date.getTime !== 'function') return null;
  return Number.isNaN(date.getTime()) ? null : date;
};

export const deriveTournamentStatus = (tournament, now = new Date()) => {
  const start = toDate(tournament?.startDate);
  const end = toDate(tournament?.endDate);
  if (start && now < start) return 'upcoming';
  if (end && now > end) return 'completed';
  return tournament?.status || 'upcoming';
};

const asText = (value) => (value === null || value === undefined ? '' : String(value).trim());

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const hasControlCharacters = (value) =>
  [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });

const asNonNegativeAmount = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : '';
  }

  const text = asText(value);
  if (!text) return '';

  // Admins have historically entered amounts as either `1,250.00`, `1.250,00`,
  // or a currency-formatted value such as `$1,250`. Keep those documents
  // readable while rejecting malformed values instead of showing NaN.
  let numeric = text.replace(/[^\d,.-]/g, '');
  if (!numeric || !/[\d]/.test(numeric)) return '';
  const comma = numeric.lastIndexOf(',');
  const dot = numeric.lastIndexOf('.');
  if (comma >= 0 && dot >= 0) {
    if (comma > dot) numeric = numeric.replace(/\./g, '').replace(',', '.');
    else numeric = numeric.replace(/,/g, '');
  } else if (comma >= 0) {
    const decimals = numeric.length - comma - 1;
    numeric = decimals === 2 ? numeric.replace(',', '.') : numeric.replace(/,/g, '');
  }

  const number = Number(numeric);
  return Number.isFinite(number) && number >= 0 ? number : '';
};

const inferCurrency = (value) => {
  const text = asText(value).toLowerCase();
  if (text.includes('€') || text.includes('eur')) return 'EUR';
  if (text.includes('£') || text.includes('gbp')) return 'GBP';
  if (text.includes('¥') || text.includes('jpy')) return 'JPY';
  if (text.includes('$') || text.includes('usd')) return 'USD';
  return '';
};

const asAmount = asNonNegativeAmount;

/**
 * Convert legacy sponsor URL strings to the canonical sponsor object shape.
 * A sponsor may be name-only, logo-only, or a linked logo.
 */
export const normalizeSponsors = (sponsors) => {
  if (!Array.isArray(sponsors)) return [];
  return sponsors
    .map((sponsor) => {
      if (typeof sponsor === 'string') {
        const logoUrl = asText(sponsor);
        return logoUrl ? { name: '', logoUrl, link: '' } : null;
      }
      if (!sponsor || typeof sponsor !== 'object') return null;
      const name = asText(sponsor.name || sponsor.title || sponsor.label);
      const logoUrl = asText(
        sponsor.logoUrl || sponsor.logo || sponsor.imageUrl || sponsor.url || sponsor.src
      );
      const link = asText(sponsor.link || sponsor.href || sponsor.website);
      if (!name && !logoUrl && !link) return null;
      return { name, logoUrl, link };
    })
    .filter(Boolean);
};

const normalizePrizeStatus = (status) => {
  const value = asText(status).toLowerCase();
  if (['published', 'visible', 'public'].includes(value)) return 'published';
  if (['undisclosed', 'unknown', 'hidden'].includes(value)) return 'undisclosed';
  return 'none';
};

const normalizeContributors = (contributors, defaultCurrency) => {
  const entries = Array.isArray(contributors)
    ? contributors
    : contributors && typeof contributors === 'object'
      ? Object.entries(contributors).map(([name, amount]) => ({ name, amount }))
      : [];
  return entries
    .map((contributor) => {
      if (typeof contributor === 'string') {
        const name = asText(contributor);
        return name ? { name, amount: '', currency: defaultCurrency } : null;
      }
      if (!contributor || typeof contributor !== 'object') return null;
      const name = asText(
        contributor.name ||
          contributor.kzcomp ||
          contributor.source ||
          contributor.label ||
          contributor.displayName
      );
      const rawAmount = firstDefined(
        contributor.amount,
        contributor.value,
        contributor.contribution,
        contributor.total
      );
      const amount = asAmount(rawAmount);
      const currency =
        asText(contributor.currency).toUpperCase() || inferCurrency(rawAmount) || defaultCurrency;
      if (!name && amount === '') return null;
      return { name, amount, currency };
    })
    .filter(Boolean);
};

/**
 * Canonical prize pool shape. Legacy top-level fields are accepted so old
 * tournament documents render without a migration.
 */
export const normalizePrizePool = (value, legacy = {}) => {
  const legacyData = legacy && typeof legacy === 'object' ? legacy : {};
  const source = value && typeof value === 'object' ? value : {};
  const legacyPool =
    value !== null && value !== undefined && typeof value !== 'object'
      ? value
      : legacyData.prizePool;
  const legacyPoolObject = legacyPool && typeof legacyPool === 'object' ? legacyPool : {};
  const directStatus =
    typeof legacyPool === 'string' &&
    ['none', 'undisclosed', 'unknown', 'hidden', 'published', 'visible', 'public'].includes(
      asText(legacyPool).toLowerCase()
    )
      ? legacyPool
      : undefined;
  const rawAmount = firstDefined(
    source.amount,
    source.total,
    source.value,
    source.prizePoolAmount,
    legacyData.prizePoolAmount,
    legacyData.prizeAmount,
    legacyData.totalPrizePool,
    legacyPoolObject.amount,
    legacyPoolObject.total,
    legacyPoolObject.value,
    typeof legacyPool === 'number' || typeof legacyPool === 'string' ? legacyPool : undefined
  );
  const amount = asAmount(rawAmount);
  const currency =
    asText(
      source.currency ||
        legacyData.prizePoolCurrency ||
        legacyData.prizeCurrency ||
        legacyPoolObject.currency ||
        inferCurrency(rawAmount)
    ).toUpperCase() || 'USD';
  const rawContributors = firstDefined(
    source.contributors,
    source.contributorBreakdown,
    source.kzcomps,
    source.kzCompContributions,
    source.contributions,
    legacyData.prizeContributors,
    legacyData.prizePoolContributors,
    legacyData.prizePoolContributions,
    legacyData.prizePoolBreakdown,
    legacyData.kzcomps,
    legacyData.kzCompContributions,
    legacyPoolObject.contributors,
    legacyPoolObject.contributions,
    legacyPoolObject.breakdown,
    []
  );
  const explicitStatus = firstDefined(
    source.status,
    source.visibility,
    source.state,
    legacyData.prizePoolStatus,
    directStatus
  );
  const hasAmount = amount !== '';
  let status = explicitStatus
    ? normalizePrizeStatus(explicitStatus)
    : hasAmount
      ? 'published'
      : 'none';

  // A few early documents stored the amount directly as `prizePool`. Treat
  // that as published, but keep an explicit status authoritative.
  if (status === 'none' && hasAmount && !explicitStatus) status = 'published';

  return {
    status,
    amount,
    currency,
    contributors: normalizeContributors(rawContributors, currency),
  };
};

const normalizeEmbed = (embed, index) => {
  if (typeof embed === 'string') {
    const url = asText(embed);
    return { label: `Bracket ${index + 1}`, url };
  }
  if (!embed || typeof embed !== 'object') return null;
  return {
    label: asText(embed.label || embed.name || embed.title) || `Bracket ${index + 1}`,
    url: asText(embed.url || embed.iframeUrl || embed.src || embed.iframe || embed.href),
  };
};

/** Normalize canonical bracket embeds and fall back to legacy iframeUrl. */
export const normalizeBracketEmbeds = (embeds, legacyIframeUrl = '') => {
  const source = Array.isArray(embeds)
    ? embeds
    : typeof embeds === 'string'
      ? [embeds]
      : embeds && typeof embeds === 'object'
        ? [embeds]
        : [];
  const normalized = source.map(normalizeEmbed).filter(Boolean);
  if (normalized.some((embed) => embed.url)) return normalized;
  const legacy = asText(legacyIframeUrl);
  return legacy ? [{ label: 'Bracket 1', url: legacy }] : [];
};

export const addBracketEmbed = (embeds) => {
  const current = Array.isArray(embeds) ? embeds : [];
  return [...current, { label: `Bracket ${current.length + 1}`, url: '' }];
};

export const removeBracketEmbed = (embeds, index) =>
  (Array.isArray(embeds) ? embeds : []).filter((_, embedIndex) => embedIndex !== index);

export const isValidExternalUrl = (value) => {
  const text = asText(value);
  if (!text || hasControlCharacters(text)) return false;
  try {
    const url = new URL(text);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Participation links pre-date the settings normalizer and may be internal
 * paths. Preserve those safe relative links while rejecting script/data URLs.
 */
export const isValidParticipationUrl = (value) => {
  const text = asText(value);
  if (!text || hasControlCharacters(text)) return false;
  if (text.startsWith('/') && !text.startsWith('//')) return true;
  return isValidExternalUrl(text);
};

/**
 * Quill's legacy alignment control stores a class on the block element. The
 * Tiptap editor stores alignment as an inline style, so translate the known
 * classes before parsing HTML. Existing explicit text-align styles win.
 */
export const normalizeLegacyRulesHtml = (html) => {
  let normalized = typeof html === 'string' ? html : '';
  ['left', 'center', 'right'].forEach((alignment) => {
    const pattern = new RegExp(
      `<([a-z0-9]+)([^>]*\\bclass=(['"])[^'"]*\\bql-align-${alignment}\\b[^'"]*\\3[^>]*)>`,
      'gi'
    );
    normalized = normalized.replace(pattern, (match, tag, attributes) => {
      if (/\bstyle=(['"])[^'"]*text-align\s*:/i.test(attributes)) return match;
      const stylePattern = /\bstyle=(['"])([^'"]*)\1/i;
      if (stylePattern.test(attributes)) {
        const withAlignment = attributes.replace(
          stylePattern,
          (_styleMatch, quote, styles) =>
            `style=${quote}${styles.replace(/\s*$/, '')}; text-align: ${alignment}${quote}`
        );
        return `<${tag}${withAlignment}>`;
      }
      return `<${tag}${attributes} style="text-align: ${alignment}">`;
    });
  });
  return normalized;
};

/**
 * The single participation rule used by listing cards and detail headers:
 * upcoming tournaments with a safe non-empty signup URL get an action; active,
 * completed, closed, and URL-less tournaments do not. Internal paths remain
 * valid for older tournament documents that linked to an in-app signup page.
 */
const registrationConfig = (tournament) => {
  const state =
    tournament?.registrationState && typeof tournament.registrationState === 'object'
      ? tournament.registrationState
      : {};
  const registration =
    tournament?.registration && typeof tournament.registration === 'object'
      ? tournament.registration
      : {};
  return { ...state, ...registration };
};

const parseParticipationOptions = (statusOrOptions, fallbackNow) => {
  if (typeof statusOrOptions === 'string') {
    return { status: statusOrOptions, now: fallbackNow };
  }
  if (statusOrOptions && typeof statusOrOptions === 'object') {
    return { ...statusOrOptions, now: statusOrOptions.now || fallbackNow };
  }
  return { now: fallbackNow };
};

/**
 * Resolve the registration gate used by both the tournament listing and the
 * detail header. Optional `joined`/`eligible` flags let a caller that already
 * has account-specific state apply the same gate without duplicating rules.
 */
export const getTournamentRegistrationState = (tournament, statusOrOptions, now = new Date()) => {
  const options = parseParticipationOptions(statusOrOptions, now);
  const resolvedStatus = options.status || deriveTournamentStatus(tournament, options.now);
  const config = registrationConfig(tournament);
  const registrationValue = tournament?.registration;
  const registrationStateValue = tournament?.registrationState;
  const url =
    [
      tournament?.participation,
      tournament?.participationUrl,
      tournament?.signupUrl,
      config.url,
      config.link,
    ]
      .map(asText)
      .find(Boolean) || '';
  const registrationStatus = asText(
    firstDefined(
      config.status,
      typeof registrationValue === 'string' ? registrationValue : undefined,
      typeof registrationStateValue === 'string' ? registrationStateValue : undefined,
      tournament?.registrationStatus,
      tournament?.participationStatus
    )
  ).toLowerCase();
  const explicitlyOpen = firstDefined(
    config.open,
    config.isOpen,
    tournament?.registrationOpen,
    tournament?.participationOpen
  );
  const explicitlyClosed =
    explicitlyOpen === false ||
    asText(explicitlyOpen).toLowerCase() === 'false' ||
    config.closed === true ||
    config.enabled === false ||
    registrationValue === false ||
    registrationStateValue === false ||
    tournament?.registrationClosed === true ||
    ['closed', 'complete', 'completed', 'full', 'unavailable'].includes(registrationStatus);
  const opensAt = toDate(
    firstDefined(
      config.opensAt,
      config.startsAt,
      tournament?.registrationStartsAt,
      tournament?.registrationStartDate,
      tournament?.registrationOpenAt
    )
  );
  const closesAt = toDate(
    firstDefined(
      config.closesAt,
      config.endsAt,
      tournament?.registrationEndsAt,
      tournament?.registrationEndDate,
      tournament?.registrationCloseAt,
      tournament?.registrationDeadline
    )
  );
  const at = toDate(options.now) || new Date();
  const joined = firstDefined(
    options.joined,
    options.alreadyJoined,
    config.joined,
    config.alreadyJoined,
    tournament?.joined,
    tournament?.alreadyJoined,
    ['joined', 'registered', 'already-joined'].includes(registrationStatus)
  );
  const eligible = firstDefined(
    options.eligible,
    config.eligible,
    tournament?.eligible,
    ['ineligible', 'not-eligible'].includes(registrationStatus) ? false : undefined
  );

  let reason = 'open';
  let open = true;
  if (!url || !isValidParticipationUrl(url)) {
    open = false;
    reason = 'missing-link';
  } else if (resolvedStatus !== 'upcoming') {
    open = false;
    reason = resolvedStatus === 'completed' ? 'completed' : 'event-started';
  } else if (explicitlyClosed) {
    open = false;
    reason = 'closed';
  } else if (opensAt && at < opensAt) {
    open = false;
    reason = 'not-open-yet';
  } else if (closesAt && at >= closesAt) {
    open = false;
    reason = 'closed';
  } else if (joined === true) {
    open = false;
    reason = 'already-joined';
  } else if (eligible === false) {
    open = false;
    reason = 'not-eligible';
  }

  return {
    open,
    reason,
    status: resolvedStatus,
    url,
    opensAt,
    closesAt,
  };
};

export const getTournamentParticipation = (tournament, statusOrOptions, now = new Date()) => {
  const state = getTournamentRegistrationState(tournament, statusOrOptions, now);
  if (!state.open) return null;
  return { url: state.url, status: state.status };
};

export const getBracketEmbeds = (tournament) => {
  const sources = [
    tournament?.bracketEmbeds,
    tournament?.externalBrackets,
    tournament?.brackets,
    tournament?.iframeUrls,
    tournament?.bracketUrls,
  ];
  for (const source of sources) {
    const embeds = normalizeBracketEmbeds(source).filter(
      (embed) => embed.url && isValidExternalUrl(embed.url)
    );
    if (embeds.length > 0) return embeds;
  }

  return normalizeBracketEmbeds(undefined, tournament?.iframeUrl).filter(
    (embed) => embed.url && isValidExternalUrl(embed.url)
  );
};

export const formatPrizeAmount = (amount, currency = 'USD') => {
  const value = asAmount(amount);
  if (value === '') return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: asText(currency).toUpperCase() || 'USD',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toLocaleString()} ${asText(currency).toUpperCase() || 'USD'}`;
  }
};
